import { TranslationRow } from '@/components/TranslationRow/TranslationRow';
import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { COMPETENCY_TRANSLATION_FIELD_MAX_LENGTH } from '@/constants/competencies/CompetencyTranslationFieldMaxLength';
import { COMPETENCY_TRANSLATION_FIELD_STYLE } from '@/constants/competencies/CompetencyTranslationFieldStyle';

interface CompetencyTranslationRowProps {
    sourceText: string;
    targetValue: string;
    onTargetChange: (value: string) => void;
    variant: CompetencyTranslationFieldKind;
}

/**
 * Decorative glyphs, not wording. Written as values rather than as JSX text so nothing invites a
 * translator to carry them — which is also what the rule against literal JSX text asks for, and the
 * reason the relocated file no longer needs the suppression the old path carried.
 */
const MARKER = {
    [CompetencyTranslationFieldKind.QUESTION_LABEL]: {
        glyph: 'Q',
        className: 'text-primary font-semibold',
    },
    [CompetencyTranslationFieldKind.OPTION_LABEL]: { glyph: '↳', className: 'text-base-300' },
} as const;

const Marker = ({ variant }: { variant: CompetencyTranslationFieldKind }) => {
    const marker = MARKER[variant as keyof typeof MARKER];

    return marker ? <span className={marker.className}>{marker.glyph}</span> : null;
};

/**
 * A translation row of the competencies panel: the shared row, told what this kind of field looks
 * like and how long it may be. The hierarchy it marks — group, competency, question, option — is
 * the competencies list's own, which is why the mapping lives here rather than in the shared row.
 */
export const CompetencyTranslationRow = ({
    sourceText,
    targetValue,
    onTargetChange,
    variant,
}: CompetencyTranslationRowProps) => (
    <TranslationRow
        sourceText={sourceText}
        targetValue={targetValue}
        onTargetChange={onTargetChange}
        maxLength={COMPETENCY_TRANSLATION_FIELD_MAX_LENGTH[variant]}
        marker={<Marker variant={variant} />}
        sourceStyle={COMPETENCY_TRANSLATION_FIELD_STYLE[variant]}
    />
);
